<template>
  <ModalStep
    id="matching-content"
    data-test-id="matching-popup-title"
    :hide-back-button="isMandatoryRef"
    :header="{
      title: titleRef,
    }"
  >
    <template #headerBeforeElement>
      <ProgramsDropdown />
    </template>

    <template #bodyInformation>
      <!-- Saisie obligatoire : on dit la conséquence, pas « prochaine étape » -->
      <p v-if="isMandatoryRef" data-test-id="matching-popup-consequence" class="matching-content__consequence">
        {{
          mentorOrMenteeMatchingTypeOpen === 'mentor'
            ? t('matching.fillingCriterias.mandatory.mentor.body')
            : t('matching.fillingCriterias.mandatory.mentee.body')
        }}
      </p>
      <div
        v-else-if="mentorOrMenteeMatchingTypeOpen === 'mentor'"
        v-html="t('matching.fillingCriterias.mentor.body')"
      ></div>
      <div v-else v-html="t('matching.fillingCriterias.mentee.body')"></div>

      <!-- Ce qui reste de l'ancienne étape « rappel d'objectif » : le contexte
           du programme, replié, sur le même écran que les champs. -->
      <Accordion v-if="programDescriptionRef" type="single" collapsible class="matching-content__program">
        <AccordionItem value="program">
          <AccordionTrigger data-test-id="matching-popup-program-details">
            {{ t('matching.fillingCriterias.programDetails', { program: programNameRef }) }}
          </AccordionTrigger>
          <AccordionContent>
            <p v-if="programDateRef" class="matching-content__program-dates">{{ programDateRef }}</p>
            <div class="matching-content__program-description" v-html="programDescriptionRef"></div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </template>

    <template #bodyContent>
      <MatchingInformationContainer />
      <p
        v-if="isMandatoryRef && missingFieldsRef.length > 0"
        data-test-id="matching-popup-missing-fields"
        class="matching-content__missing"
        aria-live="polite"
      >
        {{ t('matching.fillingCriterias.missingFields', { fields: missingFieldNamesRef }) }}
      </p>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <!-- Obligatoire : pas de « Remplir plus tard », la seule autre issue est le support -->
        <CustomButton
          v-if="isMandatoryRef"
          usage="link"
          color="white"
          :text="t('ui.button.support')"
          :link="{ url: '#brevoConversationsExpanded' }"
          data-test-id="matching-popup-support"
        />
        <CustomButton
          v-else
          usage="button"
          type="button"
          color="white"
          :text="t('ui.button.fillLater')"
          test-id="matching-popup-fill-later"
          @emit:click="fillLater"
        />
        <CustomButton
          :isLoading="isSubmittingRef"
          :isDisabled="isMandatoryRef && missingFieldsRef.length > 0"
          :text="
            mentorOrMenteeMatchingTypeOpen === 'mentor'
              ? t('matching.fillingCriterias.button.fillCriterias')
              : canAutoMatch
                ? t('matching.fillingCriterias.button.lookForMentor')
                : t('matching.fillingCriterias.button.saveCriteria')
          "
          usage="button"
          test-id="matching-popup-search-mentor"
          type="submit"
          color="primary"
          @emit:click="onSubmit"
        ></CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import useAccount from '@/features/auth/hooks/use-account.hook'
import {
  menteeFillCriteriaMutation,
  menteeMatchSuggestMentorMutation,
  mentorMatchFillCriteriaMutation,
} from '@/features/matching/api'
import MatchingInformationContainer from '@/features/matching/components/matching-modals/matching-modal-content/matching-information-container/matching-information-container.vue'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import ProgramsDropdown from '@/features/programs/components/programs-dropdown/programs-dropdown.vue'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import { selectGetMentorMatchingModal } from '@/features/ui/store/modal/modal.selectors'
import type { MenteeMatchingModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'
import { MENTOR_SEARCH_ATTEMPTS_KEY } from '@/utils/config/constants'
import { getFullTimeRange } from '@/utils/dates'
import { invalidateUser, resetMainQueriesCache } from '@/utils/lib/vue-query'

const { t } = useI18n()
const toast = useToast()

const { accountRef } = useAccount()
const mentorMachingModalRef = selectGetMentorMatchingModal(modalsStore)
const mentorOrMenteeMatchingTypeOpen = computed<'mentor' | 'mentee'>(() =>
  mentorMachingModalRef.value.isOpen ? 'mentor' : 'mentee',
)

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { canAutoMatch } = useMatchingSettings(selectedProgramIdRef)
const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)

const programNameRef = computed(() => selectedProgramRef.value?.name || '')
const programDescriptionRef = computed(() => selectedProgramRef.value?.description || '')
const programDateRef = computed(() =>
  getFullTimeRange(selectedProgramRef.value?.latestPeriod?.startAt, selectedProgramRef.value?.latestPeriod?.endAt),
)

const { mutate: mentorFillCriteria } = useMutation({
  mutationFn: mentorMatchFillCriteriaMutation,
  onError: (error) => {
    // if an error occurs, ...
    console.error(error)
    toast?.error(t('error.common'))
  },
  // we close the modal, show success and invalidate the queries
  onSuccess: async () => {
    // Validation réussie : plus d'abandon possible pour cet affichage. Posé au
    // succès et non avant l'appel, pour qu'un échec serveur laisse l'abandon tracer.
    hasSubmittedRef.value = true
    // we hard reset the cache and invalide the queries to refetch everything in background
    await invalidateUser(accountRef.value!.id!)
    await resetMainQueriesCache(selectedProgramIdRef.value!)
    toast?.success(t('matching.fillingCriterias.success'))
  },
  onSettled: () => {
    isSubmittingRef.value = false
  },
  onMutate: () => {
    isSubmittingRef.value = true
  },
})

const { mutate: menteeFillCriteria } = useMutation({
  mutationFn: menteeFillCriteriaMutation,
  onError: (error) => {
    console.error(error)
    toast?.error(t('error.common'))
  },
  onSuccess: async () => {
    hasSubmittedRef.value = true
    await invalidateUser(accountRef.value!.id!)
    await resetMainQueriesCache(selectedProgramIdRef.value!)
    toast?.success(t('matching.fillingCriterias.success'))
    modalsStore.toggleModal('mentee-matching', false)
  },
  onSettled: () => {
    isSubmittingRef.value = false
  },
  onMutate: () => {
    isSubmittingRef.value = true
  },
})

const { mutate: menteeMatchSuggestMentor } = useMutation({
  mutationFn: menteeMatchSuggestMentorMutation,
  onError: (error) => {
    if (error.message === 'No match found') {
      toast?.error(t('matching.fillingCriterias.noMentorMatching'))
    } else {
      toast?.error(t('error.common'))
    }
    // if an error occurs, we go back to the previous step (mentee)
    modalsStore.setModalStep<MenteeMatchingModalType>('mentee-matching', 'fill-criteria')
  },

  // we set the matched mentor id in the modal attached data and go to next step
  onSuccess: async (data) => {
    // Invalider le cache de l'utilisateur pour que la page /profile affiche les nouveaux critères
    await invalidateUser(accountRef.value!.id!)

    if (data.message === 'No mentor available') {
      // we set the number of attempts to find a mentor to +1
      window.localStorage.setItem(
        MENTOR_SEARCH_ATTEMPTS_KEY,
        (parseInt(window.localStorage.getItem(MENTOR_SEARCH_ATTEMPTS_KEY) || '0') + 1).toString(),
      )

      toast?.error(t('matching.fillingCriterias.noMentorAvailable'))

      modalsStore.toggleModal('mentee-matching', false)
      modalsStore.toggleModal('no-mentor-found', true)
    } else {
      // we remove the number of attempts to find a mentor and we show the matching confirmation to the user
      window.localStorage.removeItem(MENTOR_SEARCH_ATTEMPTS_KEY)

      modalsStore.setModalStep<MenteeMatchingModalType>('mentee-matching', 'matching-confirmation')
      modalsStore.updateModalAttachedData<MenteeMatchingModalType>('mentee-matching', {
        mentors: data.mentors,
        ...(data.attemptNumber !== undefined ? { attemptNumber: data.attemptNumber } : {}),
      })
    }
  },
  onSettled: () => {
    isSubmittingRef.value = false
  },
  onMutate: () => {
    isSubmittingRef.value = true
    // Auto-match : l'étape change tout de suite, ce démontage n'est pas un
    // abandon. En cas d'erreur, l'étape revient et remonte une nouvelle popup.
    hasSubmittedRef.value = true

    //  we show the loading screen (the matching will be done in the background)
    modalsStore.setModalStep<MenteeMatchingModalType>('mentee-matching', 'matching-loading')
  },
})

const onSubmitCallback = (filledCriteria: { inputId: string; value: string }[]) => {
  trackEvent(ANALYTICS_EVENTS.MATCHING_CRITERIA_COMPLETE, {
    program_id: selectedProgramIdRef.value,
    role: mentorOrMenteeMatchingTypeOpen.value,
    criteria_filled_count: filledCriteria.filter((c) => c.value.trim() !== '').length,
    criteria_total_count: filledCriteria.length,
    has_empty_fields: filledCriteria.some((c) => c.value.trim() === ''),
    mandatory: isMandatoryRef.value,
  })

  if (mentorOrMenteeMatchingTypeOpen.value === 'mentor') {
    // we fill the mentor criterias
    mentorFillCriteria({
      userId: accountRef.value!.id!,
      criteria: filledCriteria,
      programId: selectedProgramIdRef.value!,
    })
  } else {
    // mentee case: check if auto-match is enabled
    if (canAutoMatch.value) {
      // we suggest a mentor to the mentee (with auto-matching)
      menteeMatchSuggestMentor({
        criteria: filledCriteria,
        programId: selectedProgramIdRef.value!,
      })
    } else {
      // we only save the mentee criteria (without auto-matching)
      menteeFillCriteria({
        criteria: filledCriteria,
        programId: selectedProgramIdRef.value!,
      })
    }
  }
}

const {
  onSubmit,
  isSubmittingRef,
  requireAllFieldsRef,
  missingFieldsRef,
  remainingFieldsCountRef,
  totalFieldsCountRef,
} = useMatchingInformation({
  onSubmitCallback,
})

// Saisie obligatoire (mentor, ou mentee sans suggestion automatique) : tous les
// champs requis, bouton désactivé tant qu'il en manque, modale sans sortie.
const isMandatoryRef = requireAllFieldsRef

const missingFieldNamesRef = computed(() => missingFieldsRef.value.map((field) => field.name).join(', '))

const titleRef = computed(() => {
  if (!isMandatoryRef.value) {
    return mentorOrMenteeMatchingTypeOpen.value === 'mentor'
      ? t('matching.fillingCriterias.mentor.title')
      : t('matching.fillingCriterias.mentee.title')
  }
  const count = remainingFieldsCountRef.value
  const key =
    mentorOrMenteeMatchingTypeOpen.value === 'mentor'
      ? 'matching.fillingCriterias.mandatory.mentor.title'
      : 'matching.fillingCriterias.mandatory.mentee.title'
  return t(key, { count }, count)
})

function fillLater() {
  trackAbandon('fill_later')
  modalsStore.toggleShouldHideMatchingModals(true)
}

// --- Abandon de la modale (au plus un event par affichage) ---------------
// Sans lui, un parrain qui ferme l'onglet ne laisse aucune trace : depuis
// que la saisie est obligatoire, un profil partiel ne s'enregistre plus, et
// $pageleave ne part presque pas sur mobile (diagnostic Assas du 2026-09-23).
type AbandonExitMethodType =
  'tab_hidden' | 'page_unload' | 'support_click' | 'program_switch' | 'fill_later' | 'navigation'

const hasSubmittedRef = ref(false)
let abandonSent = false
let displayedAt = 0
let lastInputId: string | null = null

function trackAbandon(exitMethod: AbandonExitMethodType, programId = selectedProgramIdRef.value) {
  if (abandonSent || hasSubmittedRef.value) return
  // Onglet masqué : sortie peut-être provisoire (changement d'appli), et la
  // fermeture d'un onglet envoie visibilitychange avant pagehide. On trace sans
  // verrouiller, pour que page_unload ou une autre sortie définitive suive.
  if (exitMethod !== 'tab_hidden') abandonSent = true
  trackEvent(
    ANALYTICS_EVENTS.MATCHING_CRITERIA_MODAL_ABANDON,
    {
      program_id: programId,
      role: mentorOrMenteeMatchingTypeOpen.value,
      exit_method: exitMethod,
      last_input_id: lastInputId,
      filled_count: totalFieldsCountRef.value - remainingFieldsCountRef.value,
      missing_count: remainingFieldsCountRef.value,
      time_in_modal_ms: Date.now() - displayedAt,
    },
    { transport: 'sendBeacon' },
  )
}

function onModalFocusIn(event: FocusEvent) {
  const uuid = (event.target as HTMLElement | null)?.dataset?.uuid
  if (uuid) lastInputId = uuid
}

function onModalClick(event: MouseEvent) {
  const target = event.target as Element | null
  if (target?.closest('[data-test-id="matching-popup-support"], a[href="#brevoConversationsExpanded"]')) {
    trackAbandon('support_click')
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') trackAbandon('tab_hidden')
}

function onPageHide() {
  trackAbandon('page_unload')
}

// Changement de programme depuis la liste déroulante de l'en-tête : l'abandon
// est attribué au programme qu'on quitte.
// Si la popup reste montée (programme suivant sans critères complets), on
// repart sur un nouvel affichage ; si elle se démonte, le remontage s'en charge.
watch(selectedProgramIdRef, (_newId, oldId) => {
  if (!oldId) return
  trackAbandon('program_switch', oldId)
  nextTick(() => {
    if (isUnmounting) return
    abandonSent = false
    hasSubmittedRef.value = false
    lastInputId = null
    trackModalView()
  })
})

let isUnmounting = false

function trackModalView() {
  displayedAt = Date.now()
  trackEvent(ANALYTICS_EVENTS.MATCHING_CRITERIA_MODAL_VIEW, {
    program_id: selectedProgramIdRef.value,
    role: mentorOrMenteeMatchingTypeOpen.value,
    mandatory: isMandatoryRef.value,
    missing_count: remainingFieldsCountRef.value,
    total_count: totalFieldsCountRef.value,
  })
}

let modalElement: HTMLElement | null = null

// La modale s'ouvre désormais directement sur cet écran : son montage vaut
// affichage. Tranche la question « popup pas vue » vs « fermée » du diagnostic.
onMounted(() => {
  trackModalView()

  modalElement = document.getElementById('matching-content')
  modalElement?.addEventListener('focusin', onModalFocusIn)
  modalElement?.addEventListener('click', onModalClick, true)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)
})

onBeforeUnmount(() => {
  isUnmounting = true
  // Démontée sans validation (navigation, fermeture par le code) = abandon.
  trackAbandon('navigation')
  modalElement?.removeEventListener('focusin', onModalFocusIn)
  modalElement?.removeEventListener('click', onModalClick, true)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('pagehide', onPageHide)
})
</script>

<style lang="scss">
@import './matching-modal-content.scss';
</style>
